import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BasicInfoTab } from './tabs/BasicInfoTab';
import { OpeningHoursTab } from './tabs/OpeningHoursTab';
import { BlockedTimesTab } from './tabs/BlockedTimesTab';

interface OpeningHours {
  dayOfWeek: number;
  start: string;
  end: string;
  closed?: boolean;
}

interface BlockedTime {
  id: string;
  start: Date;
  end: Date;
  reason: string;
  recurring?: boolean;
  recurrenceEnd?: Date;
  allDay?: boolean;
}

interface BookingSettings {
  defaultDuration: number;
  maxBookingsPerDay: number;
  maxBookingDuration: number;
  minBookingNotice: number;
  maxAdvanceBooking: number;
  autoConfirm: boolean;
}

interface Facility {
  id: string;
  name: string;
  type: string;
  capacity: number;
  description: string;
  status: 'available' | 'maintenance' | 'occupied';
  openingHours: OpeningHours[];
  blockedTimes: BlockedTime[];
  bookingSettings: BookingSettings;
}

interface FacilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (facility: Omit<Facility, 'id'>) => void;
  facility?: Facility | null;
}

const DAYS_OF_WEEK = [
  'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 
  'Freitag', 'Samstag', 'Sonntag'
];

const DEFAULT_OPENING_HOURS: OpeningHours[] = DAYS_OF_WEEK.map((_, index) => ({
  dayOfWeek: index,
  start: '08:00',
  end: '20:00',
  closed: false
}));

const DEFAULT_BOOKING_SETTINGS: BookingSettings = {
  defaultDuration: 60,
  maxBookingsPerDay: 3,
  maxBookingDuration: 120,
  minBookingNotice: 60,
  maxAdvanceBooking: 14,
  autoConfirm: false
};

const TABS = [
  { id: 'basic', label: 'Basis-Informationen' },
  { id: 'hours', label: 'Öffnungszeiten' },
  { id: 'blocked', label: 'Sperrzeiten' }
] as const;

type TabId = typeof TABS[number]['id'];

export const FacilityModal: React.FC<FacilityModalProps> = ({
  isOpen,
  onClose,
  onSave,
  facility
}) => {
  const [formData, setFormData] = useState(facility || {
    name: '',
    type: 'riding-hall',
    capacity: 1,
    description: '',
    status: 'available',
    openingHours: DEFAULT_OPENING_HOURS,
    blockedTimes: [],
    bookingSettings: DEFAULT_BOOKING_SETTINGS
  });
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = isMobile ? 'hidden' : 'auto';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (facility) {
      setFormData({
        ...facility,
        blockedTimes: facility.blockedTimes || [],
        bookingSettings: {
          ...DEFAULT_BOOKING_SETTINGS,
          ...facility.bookingSettings
        }
      });
    } else {
      setFormData({
        name: '',
        type: 'riding-hall',
        capacity: 1,
        description: '',
        status: 'available',
        openingHours: DEFAULT_OPENING_HOURS,
        blockedTimes: [],
        bookingSettings: DEFAULT_BOOKING_SETTINGS
      });
    }
    setActiveTab('basic');
  }, [facility]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            formData={formData}
            onChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
          />
        );
      case 'hours':
        return (
          <OpeningHoursTab
            openingHours={formData.openingHours}
            onUpdate={(hours) => setFormData(prev => ({ ...prev, openingHours: hours }))}
          />
        );
      case 'blocked':
        return (
          <BlockedTimesTab
            blockedTimes={formData.blockedTimes}
            onUpdate={(times) => setFormData(prev => ({ ...prev, blockedTimes: times }))}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-y-auto">
      <div className={`
        min-h-full
        ${isMobile ? '' : 'py-6'}
        flex items-center justify-center
      `}>
        <div className={`
          w-full
          ${isMobile ? 'min-h-screen' : 'max-h-[calc(100vh-3rem)]'}
          ${isMobile ? '' : 'max-w-4xl'}
          bg-white dark:bg-gray-800
          ${isMobile ? '' : 'rounded-lg shadow-xl'}
          flex flex-col
        `}>
          <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {facility ? 'Anlage bearbeiten' : 'Neue Anlage hinzufügen'}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
            {isMobile ? (
              <div className="px-4 py-3">
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value as TabId)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                >
                  {TABS.map((tab) => (
                    <option key={tab.id} value={tab.id}>
                      {tab.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="px-4">
                <nav className="-mb-px flex space-x-8 overflow-x-auto">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                        ${activeTab === tab.id
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                        }
                        transition-colors duration-200
                      `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            <div className={`
              flex-1 p-4 overflow-y-auto
              ${isMobile ? 'max-h-[calc(100vh-14rem)]' : 'max-h-[calc(100vh-18rem)]'}
            `}>
              {renderTabContent()}
            </div>

            <div className="flex-shrink-0 p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors"
                >
                  {facility ? 'Änderungen speichern' : 'Anlage hinzufügen'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};